import {
    addLineItemsType,
    addSelectionFields,
    addStandardHeaderActions,
    addTableColumnExtensions,
    removeElementsFromEnum,
    retrieveNameWithoutVocabularyFromVisualization,
    getUIAnnotationForEntitySet,
    getExportTablesPropertiesAdapter
} from './utils';
import { hasPropertyTypeError } from './manifestPropertyUtils';
import { filterFlexChanges, findAnalyticalListPage } from '../import';
import {
    addFieldsType,
    createAnnotationPath,
    DEFINITION_LINK_PREFIX,
    updatePropertyIndices,
    type ManifestPathParams
} from '../../common';
import {
    v2,
    ManifestSection,
    DefinitionName,
    PropertyName,
    LogSeverity,
    MANIFESTPATH,
    SchemaTag
} from '@sap/ux-specification-types';
import { addEnumForVariantPaths } from './listReport';
import type { Definition } from 'typescript-json-schema';
import type { GenerateAppSchemaParameters } from '@sap/ux-specification-types';
import i18next from 'i18next';
import type { ExtensionLogger } from '../../..';
import { log } from '../../../extensionLogger';
import {
    UIAnnotationTerms,
    type Chart,
    type DataPoint,
    type LineItem,
    type PresentationVariant,
    type SelectionPresentationVariant
} from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { EntityTypeAnnotations_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import type * as Edm from '@sap-ux/vocabularies-types/Edm';
import { ALPTABLETYPES } from '../types';
import { FacetBase } from '../../../../../types/src/common/types';
import { pageTypes } from '../export/factory';
import { addManifestPathsToProperties, getPageManifestPath } from '../utils';
import { SAPUI5_VIEW_EXTENSION_ANALYTICAL_LIST_PAGE } from '@sap/ux-specification-types/src/v2';

const ROOT_PROPERTIES_ORDER: Array<keyof v2.AnalyticalListPageConfigV2> = [
    'filterBar',
    'keyPerformanceIndicators',
    'chart',
    'table',
    'footer'
];

/**
 * Returns LineItem and Chart annotations used for the ALP. Reproduces logic from Fiori Elements ALP implementation (function createWorkingContext in ALP specific AnnotationHelper).
 *
 * @param {v2.SapUiAppPageV2} v2Page - actual page in the manifest
 * @param {EntityTypeAnnotations_UI} uIAnnotations - annotations of UI namespace of the given entityType
 * @param logger - Logger instance used for logging messages
 * @returns {LineItem | undefined} - LineItem annotation
 */
function getLineItemAndChartAnnotation(
    v2Page: v2.SapUiAppPageV2,
    uIAnnotations: EntityTypeAnnotations_UI,
    logger?: ExtensionLogger
): { lineItemAnnotation?: LineItem; chartAnnotation?: Chart } {
    const settings = v2Page?.component?.settings;
    if (hasPropertyTypeError(settings, 'qualifier', logger)) {
        return {
            lineItemAnnotation: undefined,
            chartAnnotation: undefined
        }; // return undefined in case there is an error in the settings
    }
    const qualifier = settings?.qualifier; // qualifier from the manifest (if it has been configured)

    // retrieve the definition of the specified annotation for the qualifier which has been retrieved from the manifest settings
    const getAnnotationWithQualifier = function (annotationTerm: string) {
        const annotationPath = annotationTerm + (qualifier ? '#' + qualifier : '');
        return uIAnnotations[annotationPath];
    };
    const selectionPresentationVariant: SelectionPresentationVariant = getAnnotationWithQualifier(
        FacetBase.SelectionPresentationVariant
    );
    const presentationVariant: PresentationVariant = selectionPresentationVariant
        ? selectionPresentationVariant[FacetBase.PresentationVariant]
        : getAnnotationWithQualifier(FacetBase.PresentationVariant); // If no SPV with the given qualifier exists, then ALP code will try to identify a PV with the given qualifier directly.
    // If an SPV could be identified which does not point to an existing PV this is considered as an error
    if (selectionPresentationVariant && !presentationVariant) {
        const message = qualifier
            ? i18next.t('NOPRESENTATIONVARIANTFORSPV', { qualifier: qualifier })
            : i18next.t('NOPRESENTATIONVARIANTFORDEFAULTSPV');
        log(logger, {
            severity: LogSeverity.Error,
            message: message
        });
        return {
            lineItemAnnotation: undefined,
            chartAnnotation: undefined
        };
    }
    // If a PV could be determined LineItem and Chart should be defined as a visualization of the PV.
    const visualizations: Edm.AnnotationPath<Chart | DataPoint | LineItem>[] =
        presentationVariant?.Visualizations || [];
    let lineItemName: string = ''; // the name that will be used to retrieve the LineItem from uiAnnotations
    let chartName: string = ''; // the name that will be used to retrieve the Chart from uiAnnotations
    visualizations.some(function (visualization) {
        lineItemName =
            lineItemName || retrieveNameWithoutVocabularyFromVisualization(FacetBase.LineItem, visualization);
        chartName = chartName || retrieveNameWithoutVocabularyFromVisualization(FacetBase.Chart, visualization);
        return lineItemName && chartName; // end loop as soon as names of LineItem and Chart have both been identified
    });
    // If the above logic did not identify a name for the LineItem/Chart then still the corresponding annotation (but without qualifier) will be used
    lineItemName = lineItemName || FacetBase.LineItem;
    chartName = chartName || FacetBase.Chart;
    const lineItemAnnotation = uIAnnotations[lineItemName];
    const chartAnnotation = uIAnnotations[chartName];
    // Not having any line item/chart annotation is allowed according to FE documentation but still questionable. Hence, we log a warning.
    const warningIndex = (lineItemAnnotation || settings.quickVariantSelectionX ? 0 : 1) + (chartAnnotation ? 0 : 2);
    if (warningIndex > 0) {
        const warningKey = ['NOLINEITEMS', 'NOCHARTS', 'NOLINEITEMANDCHARTS'][warningIndex - 1];
        const message = i18next.t(warningKey);
        log(logger, {
            severity: LogSeverity.Warning,
            message: message
        });
    }
    return {
        lineItemAnnotation,
        chartAnnotation
    };
}

/**
 * Adds missing annotation path to chart definition.
 *
 * @param {Definition} appSchema - app specific JSON schema
 * @param {Chart} chartAnnotation - chart annotation data
 */
function addChart(appSchema: Definition, chartAnnotation: Chart): void {
    const chartSettings = appSchema.definitions[DefinitionName.ChartSettings] as Definition;
    const pathParts = chartAnnotation.fullyQualifiedName.split('@');
    chartSettings[SchemaTag.annotationPath] = `/${pathParts[0]}/@${pathParts[1]}`;
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param appSchema - Full schema.
 * @param definitionName - The name of the definition/class whose properties are being processed.
 * @param definition - The definition object containing property schemas.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToPropertiesALP(
    appSchema: Definition,
    definitionName: string | undefined,
    definition: Definition,
    pathParams: ManifestPathParams
): void {
    let targetAnnotation: string | undefined = undefined;
    if (pathParams.targetAnnotation) {
        const annotationParts = pathParams.targetAnnotation?.split('/');
        targetAnnotation = annotationParts[annotationParts.length - 1];
    } else {
        // Default annotation path
        targetAnnotation = `@${UIAnnotationTerms.LineItem}`;
    }
    addManifestPathsToProperties(
        appSchema,
        definitionName,
        definition,
        {
            ...pathParams,
            pathsParts: [targetAnnotation, '', targetAnnotation]
        },
        pageTypes.AnalyticalListPage
    );
}

/**
 * Adds 'manifestPath' to static nodes in schema.
 *
 * @param appSchema - Full schema.
 * @param pathParams - Additional context required to compute manifest paths.
 */
function addManifestPathsToStaticNodes(appSchema: Definition, pathParams: ManifestPathParams): void {
    // Add "manifestPath" for current page
    appSchema['manifestPath'] = getPageManifestPath(pathParams);
    // Add manifestPath to root level
    addManifestPathsToPropertiesALP(appSchema, 'AnalyticalListPage', appSchema, pathParams);
    // Add manifestPath to chart
    addManifestPathsToPropertiesALP(appSchema, undefined, appSchema.properties.chart as Definition, pathParams);
    // Add manifestPath to table
    addManifestPathsToPropertiesALP(appSchema, undefined, appSchema.properties.table as Definition, pathParams);
    // Add manifestPath to filterBar
    addManifestPathsToPropertiesALP(appSchema, undefined, appSchema.properties.filterBar as Definition, pathParams);
}

/**
 * Generates an app specific schema out of the generic schema.
 * Generic types are replaced by information from the app specific annotations.
 *
 * @param {GenerateAppSchemaParameters} generateParameters - list of API input parameters
 * @param genericSchema - generic JSON schema of a list report
 * @returns {object} - app specific JSON schema
 */
export function generateAnalyticalListReportSchemaV2(
    generateParameters: GenerateAppSchemaParameters,
    genericSchema: object
): object {
    // Ensure that only those flex changes are used that refer to the ALP
    generateParameters = {
        ...generateParameters,
        flex:
            generateParameters.flex && generateParameters.entitySet
                ? filterFlexChanges(
                      generateParameters.flex,
                      SAPUI5_VIEW_EXTENSION_ANALYTICAL_LIST_PAGE,
                      generateParameters.entitySet.name
                  )
                : []
    };
    const appSchema = JSON.parse(JSON.stringify(genericSchema)) as Definition;

    (appSchema.properties[PropertyName.table] as Definition).anyOf[0]['$ref'] =
        DEFINITION_LINK_PREFIX + 'ALPResponsiveTable<LineItems>';
    const tableDefinitionResponsive = JSON.parse(
        JSON.stringify(appSchema.definitions.ALPResponsiveTable)
    ) as Definition;
    tableDefinitionResponsive.properties['columns']['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.LineItems}`;
    tableDefinitionResponsive.properties['toolBar']['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.ToolBarLR}`;
    appSchema.definitions['ALPResponsiveTable<LineItems>'] = tableDefinitionResponsive;

    appSchema.properties[PropertyName.table]['anyOf'][1]['$ref'] = `${DEFINITION_LINK_PREFIX}ALPGridTable<LineItems>`;

    try {
        const tableDefinitionGrid = JSON.parse(JSON.stringify(appSchema['definitions']['ALPGridTable'])) as Definition;
        tableDefinitionGrid.properties.columns['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.LineItems}`;
        tableDefinitionGrid.properties.toolBar['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.ToolBarLR}`;
        appSchema.definitions['ALPGridTable<LineItems>'] = tableDefinitionGrid;
    } catch {
        throw new Error(
            i18next.t('INCONSISTENTSCHEMA', {
                function: 'generateAnalyticalListReportSchemaV2',
                definition: 'ALPGridTable'
            })
        );
    }

    appSchema.properties[PropertyName.table]['anyOf'][2]['$ref'] =
        `${DEFINITION_LINK_PREFIX}ALPAnalyticalTable<LineItems>`;
    const tableDefinitionAnalytical = JSON.parse(
        JSON.stringify(appSchema.definitions['ALPAnalyticalTable'])
    ) as Definition;
    tableDefinitionAnalytical.properties.columns['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.LineItems}`;
    tableDefinitionAnalytical.properties.toolBar['$ref'] = `${DEFINITION_LINK_PREFIX}${DefinitionName.ToolBarLR}`;
    appSchema.definitions['ALPAnalyticalTable<LineItems>'] = tableDefinitionAnalytical;

    // Remove Custom Column non existing extensions - TreeTableColumnsExtension does not exist in ALP
    removeElementsFromEnum(appSchema.definitions.TableColumnExtensionTypeV2['enum'], [
        v2.TableColumnExtensionTypeV2.TreeTableColumnsExtension
    ]);

    const pageKeys = [];
    const v2Page = findAnalyticalListPage(
        generateParameters.manifest[ManifestSection.generic].pages as v2.SapUiAppPagesV2,
        pageKeys,
        generateParameters.logger
    );
    // Inject special logic for property useExportToExcel into schema section for all table types.
    const exportTablePropertiesAdapter = getExportTablesPropertiesAdapter(generateParameters.flex ?? []);
    ALPTABLETYPES.forEach((tableType) => {
        const tableDefinition = appSchema.definitions[tableType] as Definition;
        exportTablePropertiesAdapter(tableDefinition);
    });

    const { entityType, uIAnnotations } = getUIAnnotationForEntitySet(generateParameters); // would log an error in case these entities are missing
    if (uIAnnotations) {
        // then entityType is there as well
        // handle header actions
        addStandardHeaderActions(appSchema);

        const { lineItemAnnotation, chartAnnotation } = getLineItemAndChartAnnotation(
            v2Page,
            uIAnnotations,
            generateParameters.logger
        );

        //handle line items
        if (lineItemAnnotation) {
            ALPTABLETYPES.forEach((tableType) => {
                appSchema.definitions[tableType][SchemaTag.annotationPath] = createAnnotationPath(
                    entityType.fullyQualifiedName,
                    lineItemAnnotation.term,
                    lineItemAnnotation.qualifier
                );
            });
        }
        addLineItemsType(false, appSchema, lineItemAnnotation, entityType);

        // handle chart item
        if (chartAnnotation) {
            addChart(appSchema, chartAnnotation);
        }

        //handle filter fields
        const selectionFieldAnnotation = uIAnnotations.SelectionFields;
        addSelectionFields(appSchema, selectionFieldAnnotation, entityType);

        //add fields for createWithParameterDialog
        addFieldsType(appSchema, entityType);

        if (!v2Page) {
            log(generateParameters.logger, {
                severity: LogSeverity.Error,
                message: i18next.t('NOLR'),
                location: {
                    path: MANIFESTPATH,
                    range: [ManifestSection.generic]
                }
            });
            return appSchema;
        }

        addTableColumnExtensions(
            appSchema,
            entityType,
            v2Page,
            generateParameters.manifest,
            generateParameters.fragments
        );
        addEnumForVariantPaths(entityType, generateParameters.entitySet, appSchema, generateParameters.serviceAVT);

        // Delete generic definitions that have been replaced
        delete appSchema.definitions.ALPGridTable;
        delete appSchema.definitions.ALPResponsiveTable;
        delete appSchema.definitions.ALPAnalyticalTable;
        delete appSchema.definitions.GenericColumns;
        delete appSchema.definitions.TableColumnAction;
        delete appSchema.definitions.ToolBar;
        delete appSchema.definitions.GenericFooter;
        delete appSchema.definitions.Actions;
        delete appSchema.definitions.FieldPath;
    }
    // Add manifest path to static nodes in schema
    addManifestPathsToStaticNodes(appSchema, {
        manifest: generateParameters.manifest,
        pageName: pageKeys
    });
    updatePropertyIndices(appSchema, ROOT_PROPERTIES_ORDER);
    return appSchema;
}
